<template>
  <div class="weather-container">
    <h2 class="title">实时天气</h2>
    
    <div v-if="loading" class="loading">
      <div class="loader"></div>
      <p>正在获取位置信息...</p>
    </div>

    <div v-else-if="error" class="error">
      <span class="icon">⚠️</span>
      <p>{{ error }}</p>
      <button @click="retry" class="retry-btn">重试</button>
    </div>

    <div v-else class="weather-info">
      <div class="location">
        <span class="icon">📍</span>
        <h3>{{ weatherData.province }} {{ weatherData.city }}</h3>
      </div>
      
      <div class="detail-grid">
        <div class="detail-item">
          <span class="icon">🌡️</span>
          <div>
            <label>温度</label>
            <p>{{ weatherData.temperature }}℃</p>
          </div>
        </div>
        
        <div class="detail-item">
          <span class="icon">🌤️</span>
          <div>
            <label>天气</label>
            <p>{{ weatherData.weather }}</p>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">💨</span>
          <div>
            <label>风力</label>
            <p>{{ weatherData.windPower }}</p>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">💧</span>
          <div>
            <label>湿度</label>
            <p>{{ weatherData.humidity }}%</p>
          </div>
        </div>
      </div>

      <p class="update-time">更新时间：{{ weatherData.reportTime }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const weatherApi = axios.create({
  baseURL: process.env.VUE_APP_API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});
export default {
    data() {
    return {
      loading: true,
      error: null,
      weatherData: null,
    };
  },
  async mounted() {
    await this.fetchWeather();
  },
  methods: {
    async fetchWeather() {
      try {
        const position = await this.getPosition();
        const { data } = await weatherApi.get('/weather', {
          params: {
            lat: position.latitude,
            lon: position.longitude
          },
          // 开发环境忽略SSL验证
          httpsAgent: process.env.NODE_ENV === 'development' ? 
            { rejectUnauthorized: false } : undefined
        });

        if (data.code === 'SUCCESS') {
          this.weatherData = data.data;
        } else {
          throw new Error(data.message || '服务返回异常');
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },

    async getPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('您的浏览器不支持地理位置功能'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          position => resolve({
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }),
          error => reject(this.parseGeolocationError(error)),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
    },

    parseGeolocationError(error) {
      const messages = {
        1: '用户拒绝了位置请求',
        2: '无法获取当前位置',
        3: '请求超时'
      };
      return new Error(messages[error.code] || '位置服务不可用');
    },

    handleError(error) {
      let errorMessage = '获取天气数据失败，';
      
      if (error.response) {
        // 服务器返回的错误
        errorMessage += `（状态码：${error.response.status}）`;
      } else if (error.request) {
        // 请求已发送但无响应
        errorMessage = '无法连接到天气服务，请检查：';
        errorMessage += '\n1. 本地网络连接\n2. 服务器是否运行\n3. 防火墙设置';
      } else {
        // 其他错误
        errorMessage += error.message;
      }

      console.error('完整错误信息:', {
        config: error.config,
        code: error.code,
        stack: error.stack
      });
      
      this.error = errorMessage;
      this.weatherData = null;
    },

    retry() {
      this.fetchWeather();
    }
  }
};
</script>

<style scoped>
.weather-container {
  max-width: 600px;
  margin: 2rem auto;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.title {
  color: #2c3e50;
  text-align: center;
  margin-bottom: 0.5rem;
}

.loading {
  text-align: center;
  padding: 2rem;
}

.loader {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 2rem;
  background: #ffeef0;
  border-radius: 8px;
  color: #dc3545;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}

.retry-btn:hover {
  background: #2980b9;
}

.weather-info {
  background: white;
  padding: 1rem;
  border-radius: 8px;
}

.location {
  text-align: center;
  margin-bottom: 1.5rem;
}

.location h3 {
  margin: 0.5rem 0;
  color: #34495e;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  justify-content: center;
}

.detail-item .icon {
  font-size: 1.5rem;
  margin-right: 0.8rem;
}

.detail-item label {
  display: block;
  font-size: 0.9rem;
  color: #7f8c8d;
}

.detail-item p {
  margin: 0.3rem 0 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: #2c3e50;
}

.update-time {
  text-align: center;
  font-size: 0.9rem;
  color: #95a5a6;
  margin: 1rem 0 0;
}
</style>